import './coapplicantEditResidencePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { USStates } from '../../../constants/states';
import { housingTypeOptions } from '../../../store/applicantStore/constants/housingTypeOptions';
import { monthsAtResidenceOptions } from '../../../store/applicantStore/constants/monthsAtResidenceOptions';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantCurrentResidenceCity,
    coapplicantCurrentResidenceLineOne,
    coapplicantCurrentResidenceLineTwo,
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    coapplicantCurrentResidenceMonthsAt,
    coapplicantCurrentResidencePostalCode,
    coapplicantCurrentResidenceState,
    coapplicantCurrentResidenceType,
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceCity,
    setCoapplicantCurrentResidenceLineOne,
    setCoapplicantCurrentResidenceLineTwo,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    setCoapplicantCurrentResidenceMonthsAt,
    setCoapplicantCurrentResidencePostalCode,
    setCoapplicantCurrentResidenceState,
    setCoapplicantCurrentResidenceType,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const shouldShowPreviousResidenceFields =
    watch(
      'currentMonthsAtResidenceValue',
      coapplicantCurrentResidenceMonthsAt,
    ) === ONE_YEAR_IN_MONTHS_AS_STRING;

  const housingTypeValidation = {
    required: t('validation.housingType.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const monthsAtResidenceValidation = {
    required: t('validation.housingType.required'),
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentCityValue,
      currentLineOneValue,
      currentLineTwoValue,
      currentMonthsAtResidenceValue,
      currentPostalCodeValue,
      currentStateValue,
      housingAmountValue,
      housingTypeValue,
      previousCityValue,
      previousLineOneValue,
      previousLineTwoValue,
      previousPostalCodeValue,
      previousStateValue,
    } = values;

    if (
      !currentCityValue ||
      !currentLineOneValue ||
      !currentMonthsAtResidenceValue ||
      !currentPostalCodeValue ||
      !currentStateValue ||
      !housingAmountValue ||
      !housingTypeValue
    ) {
      return;
    }

    if (
      shouldShowPreviousResidenceFields &&
      (!previousLineOneValue ||
        !previousCityValue ||
        !previousStateValue ||
        !previousPostalCodeValue)
    ) {
      return;
    }

    setCoapplicantCurrentResidenceCity(currentCityValue);
    setCoapplicantCurrentResidenceLineOne(currentLineOneValue);
    setCoapplicantCurrentResidenceLineTwo(currentLineTwoValue);
    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCoapplicantCurrentResidenceMonthsAt(currentMonthsAtResidenceValue);
    setCoapplicantCurrentResidencePostalCode(currentPostalCodeValue);
    setCoapplicantCurrentResidenceState(currentStateValue);
    setCoapplicantCurrentResidenceType(housingTypeValue);

    if (shouldShowPreviousResidenceFields) {
      setCoapplicantPreviousResidenceCity(previousCityValue);
      setCoapplicantPreviousResidenceLineOne(previousLineOneValue);
      setCoapplicantPreviousResidenceLineTwo(previousLineTwoValue);
      setCoapplicantPreviousResidencePostalCode(previousPostalCodeValue);
      setCoapplicantPreviousResidenceState(previousStateValue);
    } else {
      setCoapplicantPreviousResidenceCity(null);
      setCoapplicantPreviousResidenceLineOne(null);
      setCoapplicantPreviousResidenceLineTwo(null);
      setCoapplicantPreviousResidencePostalCode(null);
      setCoapplicantPreviousResidenceState(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-residence-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantEditResidence.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEditResidence.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEditResidence.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEditResidence.progress.root}
            value={coapplicantReviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEditResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditResidence.form.h1}>
                {t('CoapplicantEditResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditResidence.form.h2}>
                {t('CoapplicantEditResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('housingTypeValue', housingTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.housingTypeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditResidence.form
                          .residenceTypeSelect,
                    }}
                    label={t('CoapplicantEditResidencePage.housingType')}
                    value={watch(
                      'housingTypeValue',
                      coapplicantCurrentResidenceType || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditResidencePage.housingType')}
                    </MenuItem>

                    {housingTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.housingTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .residenceTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingTypeValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    defaultValue={inputEventHandler.currency.formatter(
                      coapplicantCurrentResidenceMonthlyPaymentAmount,
                    )}
                    error={!isEmpty(errors.housingAmountValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditResidence.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('CoapplicantEditResidencePage.housingAmount')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('currentLineOneValue', addressValidation)}
                    autoComplete="address-line1"
                    defaultValue={coapplicantCurrentResidenceLineOne}
                    error={!isEmpty(errors.currentLineOneValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditResidence.form
                          .addressLine1Input,
                    }}
                    label={t('CoapplicantEditResidencePage.residenceLineOne')}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.currentLineOneValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .addressLine1InputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.currentLineOneValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentLineTwoValue')}
                        autoComplete="address-line2"
                        defaultValue={coapplicantCurrentResidenceLineTwo}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditResidence.form
                              .addressLine2Input,
                        }}
                        label={t(
                          'CoapplicantEditResidencePage.residenceLineTwo',
                        )}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('currentCityValue', cityValidation)}
                        autoComplete="address-level2"
                        defaultValue={coapplicantCurrentResidenceCity}
                        error={!isEmpty(errors.currentCityValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditResidence.form
                              .cityInput,
                        }}
                        label={t('CoapplicantEditResidencePage.residenceCity')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.currentCityValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentCityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentStateValue', stateValidation)}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        autoComplete="address-level1"
                        error={!isEmpty(errors.currentStateValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditResidence.form
                              .stateSelect,
                        }}
                        label={t('CoapplicantEditResidencePage.residenceState')}
                        value={watch(
                          'currentStateValue',
                          coapplicantCurrentResidenceState || '',
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantEditResidencePage.residenceState')}
                        </MenuItem>

                        {USStates.map((stateObj) => {
                          return (
                            <MenuItem
                              key={stateObj.abbreviation}
                              value={stateObj.abbreviation}
                            >
                              {stateObj.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.currentStateValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .stateSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentStateValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'currentPostalCodeValue',
                          postalCodeValidation,
                        )}
                        autoComplete="postal-code"
                        defaultValue={coapplicantCurrentResidencePostalCode}
                        error={!isEmpty(errors.currentPostalCodeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditResidence.form
                              .zipCodeInput,
                          inputMode: 'numeric',
                        }}
                        label={t('CoapplicantEditResidencePage.zip')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.currentPostalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentPostalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'currentMonthsAtResidenceValue',
                      monthsAtResidenceValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.currentMonthsAtResidenceValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditResidence.form.monthSelect,
                    }}
                    label={t('CoapplicantEditResidencePage.duration')}
                    value={watch(
                      'currentMonthsAtResidenceValue',
                      coapplicantCurrentResidenceMonthsAt || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditResidencePage.duration')}
                    </MenuItem>

                    {monthsAtResidenceOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.currentMonthsAtResidenceValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .monthSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.currentMonthsAtResidenceValue.message)}
                    </span>
                  )}
                </div>
              </div>

              {shouldShowPreviousResidenceFields && (
                <div
                  data-testid="previous-residence-section"
                  style={{ marginBottom: '3rem' }}
                >
                  <h3>{t('CoapplicantEditResidencePage.previousResidence')}</h3>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('previousLineOneValue', addressValidation)}
                      autoComplete="address-line1"
                      defaultValue={coapplicantPreviousResidenceLineOne}
                      error={!isEmpty(errors.previousLineOneValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditResidence.form
                            .previousAddressLine1Input,
                      }}
                      label={t('CoapplicantEditResidencePage.residenceLineOne')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.previousLineOneValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditResidence.form
                            .previousAddressLine1InputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousLineOneValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousLineTwoValue')}
                          autoComplete="address-line2"
                          defaultValue={coapplicantPreviousResidenceLineTwo}
                          inputProps={{
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .previousAddressLine2Input,
                          }}
                          label={t(
                            'CoapplicantEditResidencePage.residenceLineTwo',
                          )}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register('previousCityValue', cityValidation)}
                          autoComplete="address-level2"
                          defaultValue={coapplicantPreviousResidenceCity}
                          error={!isEmpty(errors.previousCityValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .previousCityInput,
                          }}
                          label={t(
                            'CoapplicantEditResidencePage.residenceCity',
                          )}
                          variant="outlined"
                          fullWidth
                        />

                        {!isEmpty(errors.previousCityValue) && (
                          <span
                            data-testid={
                              locator.pages.coapplicantEditResidence.form
                                .previousCityInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousCityValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousStateValue', stateValidation)}
                          SelectProps={{
                            IconComponent: ExpandMoreRoundedIcon,
                          }}
                          autoComplete="address-level1"
                          error={!isEmpty(errors.previousStateValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .previousStateSelect,
                          }}
                          label={t(
                            'CoapplicantEditResidencePage.residenceState',
                          )}
                          value={
                            watch(
                              'previousStateValue',
                              coapplicantPreviousResidenceState || '',
                            ) || ''
                          }
                          fullWidth
                          select
                        >
                          <MenuItem value="" disabled>
                            {t('CoapplicantEditResidencePage.residenceState')}
                          </MenuItem>

                          {USStates.map((stateObj) => {
                            return (
                              <MenuItem
                                key={uniqueId(stateObj.abbreviation)}
                                value={stateObj.abbreviation}
                              >
                                {stateObj.name}
                              </MenuItem>
                            );
                          })}
                        </TextField>

                        {!isEmpty(errors.previousStateValue) && (
                          <span
                            data-testid={
                              locator.pages.coapplicantEditResidence.form
                                .previousStateSelectError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousStateValue.message)}
                          </span>
                        )}
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register(
                            'previousPostalCodeValue',
                            postalCodeValidation,
                          )}
                          autoComplete="postal-code"
                          defaultValue={coapplicantPreviousResidencePostalCode}
                          error={!isEmpty(errors.previousPostalCodeValue)}
                          inputProps={{
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .previousZipCodeInput,
                            inputMode: 'numeric',
                          }}
                          label={t('CoapplicantEditResidencePage.zip')}
                          type="number"
                          variant="outlined"
                          fullWidth
                          onKeyDown={inputEventHandler.number.keydown}
                          onPasteCapture={inputEventHandler.number.pasteCapture}
                        />

                        {!isEmpty(errors.previousPostalCodeValue) && (
                          <span
                            data-testid={
                              locator.pages.coapplicantEditResidence.form
                                .previousZipCodeInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousPostalCodeValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditResidencePage.cancel')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditResidencePage.save')}
                  </LoadingButton>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditResidencePage';
